import { useLocation, useNavigate } from "react-router";
import { backendUrl } from "../../constants";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPayoutAction, payProviderAction } from "../../Action/payouts";
import { RoutesPaths } from "../../Router/constant";

export default function Payout() {
  const location = useLocation();
  const { payoutId } = location.state || {};
  const { payout = null } = useSelector((state) => state.payouts ?? {});
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!payoutId) return;
    dispatch(getPayoutAction(payoutId));
  }, [dispatch, payoutId, location.key]);

  const [showPopup, setShowPopup] = useState(false);
  const [amount, setAmount] = useState("");
  const [initialAmount, setInitialAmount] = useState(null);
  const inputRef = useRef(null);

  const handleError = (event) => {
    event.target.src = "assets/img/dummy-image-square.jpg"; // fallback image
  };

  const payToProvider = () => {
    dispatch(payProviderAction(payout._id, amount)).then(() => {
      navigate(RoutesPaths.PAYOUTS)
      setShowPopup(false);
    });
  };

  const handleShow = (amt) => {
    const v = String(amt ?? "");
    setInitialAmount(Number(amt ?? 0));
    setAmount(v);
    setShowPopup(true);
  };

  const handleClose = () => setShowPopup(false);

  // Keep caret stable while enforcing digits-only
  const handleAmountChange = (e) => {
    const { value, selectionStart } = e.target;
    const cleaned = value.replace(/[^\d]/g, "");
    const removed = value.length - cleaned.length;

    setAmount(cleaned);

    requestAnimationFrame(() => {
      if (inputRef.current) {
        const nextPos = Math.max(
          0,
          Math.min((selectionStart ?? cleaned.length) - removed, cleaned.length)
        );
        inputRef.current.setSelectionRange(nextPos, nextPos);
        inputRef.current.focus(); // ensure focus stays
      }
    });
  };

  // Validations
  const isRequiredOk = amount.trim() !== "";
  const isNumericOk = /^\d+$/.test(amount);
  const isNonZeroOk = isNumericOk && Number(amount || "0") > 0;
  const isMaxOk =
    initialAmount == null ? true : Number(amount || "0") <= Number(initialAmount);
  const canSubmit = isRequiredOk && isNumericOk && isNonZeroOk && isMaxOk && showPopup;

  const AlertPopup = () => (
    <div
      className={`modal modal-confirmation ${showPopup ? "show" : ""}`}
      style={{ display: showPopup ? "block" : "none" }} // keep DOM, just hide
    >
      <div className="modal-content">
        <span className="close" onClick={handleClose}>
          &times;
        </span>
        <h2>Confirmation</h2>
        <p>Are you sure you want to pay amount to the provider?</p>

        <div className="row mb-8">
          <div className="col-xl-12">
            <div className="fs-6 fw-semibold mt-2 mb-3">
              Amount*{" "}
              {initialAmount != null && (
                <small className="text-muted"> (Max: {initialAmount})</small>
              )}
            </div>
          </div>

          <div className="col-xl-12 fv-row">
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-solid"
              placeholder="Enter amount..."
              value={amount}
              onChange={handleAmountChange}
              inputMode="numeric"
              pattern="\d*"
              onPaste={(e) => {
                const text = e.clipboardData.getData("text");
                if (/[^\d]/.test(text)) e.preventDefault();
              }}
            />
            {!isRequiredOk && (
              <div className="text-danger mt-2">Amount is required.</div>
            )}
            {isRequiredOk && isNumericOk && !isNonZeroOk && (
              <div className="text-danger mt-2">Amount must be greater than 0.</div>
            )}
            {isRequiredOk && !isNumericOk && (
              <div className="text-danger mt-2">Numbers only.</div>
            )}
            {isRequiredOk && isNumericOk && !isMaxOk && (
              <div className="text-danger mt-2">
                Amount cannot be greater than {initialAmount}.
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="secondary" onClick={handleClose}>
            No
          </button>
          <button
            type="button"
            className={`primary ${!canSubmit ? "disabled" : ""}`}
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) return; // hard guard
              payToProvider()
            }}
            style={!canSubmit ? { pointerEvents: "none" } : undefined} // optional: extra safety for some CSS libs
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );


  return (
    <>
      <AlertPopup />
      {/*begin::Main*/}
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="d-flex flex-column flex-column-fluid">
          <div id="kt_app_content" className="app-content flex-column-fluid">
            <div
              id="kt_app_content_container"
              className="app-container container-fluid"
            >
              <div className="card mb-5 mb-xxl-8 mt-10">
                <div className="card-body pt-9 pb-6">
                  <div className="d-flex flex-wrap flex-sm-nowrap">
                    {/* Pic */}
                    <div className="me-7 mb-4">
                      <div className="symbol symbol-100px symbol-lg-140px symbol-fixed position-relative">
                        <img
                          src={backendUrl + payout?.provider?.profilePic}
                          alt=""
                          onError={handleError}
                        />
                      </div>
                    </div>
                    {/* Info */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-2">
                            <div className="text-gray-900 fs-2 fw-bold me-1">
                              {payout?.provider?.fullName}
                            </div>
                          </div>

                          <div className="flex-wrap fw-semibold fs-6 mb-4 pe-2">
                            <div className="d-flex align-items-center text-gray-500 mb-2">
                              <i className="ki-outline ki-sms fs-4 me-1" />
                              {payout?.provider?.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Info */}
                  </div>
                </div>

                {/* Details */}
                <div className="card" id="kt_profile_details_view">
                  <div className="card-header">
                    <div className="card-title m-0">
                      <h3 className="fw-bold m-0">Payout request details</h3>
                    </div>
                  </div>

                  <div className="card-body p-9">
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">
                        Mobile number
                      </label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">
                          +27{payout?.provider?.mobileNumber}
                        </span>
                      </div>
                    </div>

                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">
                        Amount
                      </label>
                      <div className="col-lg-8 fv-row">
                        <span className="fw-semibold text-gray-800 fs-6">
                          {payout?.amount}
                        </span>
                      </div>
                    </div>

                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">
                        Status
                      </label>
                      <div className="col-lg-8 d-flex align-items-center">
                        <span
                          className={`badge fw-bold fs-6 me-2 ${payout?.status === "1"
                            ? "badge-primary"
                            : payout?.status === "2"
                              ? "badge-success"
                              : payout?.status === "3"
                                ? "badge-danger"
                                : ""
                            }`}
                        >
                          {payout?.status === "1"
                            ? "Pending"
                            : payout?.status === "2"
                              ? "Paid"
                              : payout?.status === "3"
                                ? "Canceled"
                                : ""}
                        </span>
                      </div>
                    </div>

                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">
                        Type
                      </label>
                      <div className="col-lg-8">
                        <span className="fw-semibold fs-6 text-gray-800 text-hover-primary">
                          {payout?.paymentType === "ewallet" ? "eWallet" : "Bank"}
                        </span>
                      </div>
                    </div>

                    {payout?.eWalletNumber ? (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">
                          eWallet number
                        </label>
                        <div className="col-lg-8">
                          <span className="fw-semibold fs-6 text-gray-800">
                            {payout?.eWalletNumber}
                          </span>
                        </div>
                      </div>
                    ) : null}

                    {payout?.paymentType === "bank" ? (
                      <>
                        <div className="row mb-7">
                          <label className="col-lg-4 fw-semibold text-muted">
                            Account name
                          </label>
                          <div className="col-lg-8">
                            <span className="fw-semibold fs-6 text-gray-800">
                              {payout?.bank?.accountName}
                            </span>
                          </div>
                        </div>
                        <div className="row mb-7">
                          <label className="col-lg-4 fw-semibold text-muted">
                            Account number
                          </label>
                          <div className="col-lg-8">
                            <span className="fw-semibold fs-6 text-gray-800">
                              {payout?.bank?.accountNumber}
                            </span>
                          </div>
                        </div>
                        <div className="row mb-7">
                          <label className="col-lg-4 fw-semibold text-muted">
                            Account type
                          </label>
                          <div className="col-lg-8">
                            <span className="fw-semibold fs-6 text-gray-800">
                              {payout?.bank?.accountType}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : null}

                    {payout?.status === "1" ? (
                      <div className="row mb-0 card-footer py-6 px-0">
                        <div className="col-lg-12 d-flex justify-content-end">
                          <button
                            id="kt_account_deactivate_account_submit"
                            type="button"
                            className="btn btn-primary fw-semibold"
                            onClick={() => handleShow(payout?.amount)}
                          >
                            Pay
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                {/* /Details */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*end:::Main*/}
    </>
  );
}